import { useEffect, useMemo, useState } from 'react';
import {
  AUTOMATIONS,
  resolveAutomationSetting,
  type AutomationAudience,
  type AutomationDefinition,
  type AutomationRun,
  type AutomationSchedule,
  type AutomationSetting,
  type UpdateAutomationInput,
} from '@peles/shared';
import { useDialog } from '../../context/DialogContext';
import { useAutomationRuns, useAutomations, useUpdateAutomation } from '../../hooks/useAutomations';
import { EntityList, StatusBadge, type EntityListColumn } from '../common';
import { SettingToggle } from './SettingToggle';

const AUDIENCE_LABELS: Record<AutomationAudience, string> = {
  office: 'The office',
  assignee: 'Whoever it is assigned to',
  client: "The event's clients",
  vendor: 'The vendor',
};

const GROUPS: { title: string; note: string; audiences: AutomationAudience[] }[] = [
  {
    title: 'To the office',
    note: 'On unless you turn them off. Each person can also turn one off for themselves in their notification preferences.',
    audiences: ['office', 'assignee'],
  },
  {
    title: 'To clients and vendors',
    note: 'Off until you turn them on — nothing starts writing to people outside the office without you choosing it.',
    audiences: ['client', 'vendor'],
  },
];

const RUN_STATUS_COLORS: Record<AutomationRun['status'], string> = {
  sent: '#22c55e',
  simulated: '#3b82f6',
  skipped: '#94a3b8',
  failed: '#ef4444',
};

const RUNS_PAGE_SIZE = 10;

const labelOf = (key: string) => AUTOMATIONS.find((a) => a.key === key)?.label ?? key;

function recipientText(recipient: string): string {
  if (recipient.startsWith('email:')) return recipient.slice('email:'.length);
  if (recipient.startsWith('user:')) return 'A member of the office';
  return recipient;
}

/**
 * Settings → Messaging → Automations: the messages the app sends by itself,
 * each with its switch, who it goes to and what sets it off, and a log of what
 * went out. The wording of each is edited in the templates below, under the
 * names each row lists.
 */
export function AutomationsPanel() {
  const { data: stored, isLoading, isError } = useAutomations();
  const update = useUpdateAutomation();
  const { alert } = useDialog();

  const settings = useMemo(() => {
    const byKey = new Map((stored ?? []).map((s) => [s.key, s]));
    return new Map(
      AUTOMATIONS.map((a) => [a.key, byKey.get(a.key) ?? resolveAutomationSetting(a.key)]),
    );
  }, [stored]);

  const save = (definition: AutomationDefinition, input: UpdateAutomationInput) => {
    update.mutate(
      { key: definition.key as AutomationSetting['key'], input },
      {
        onError: () =>
          void alert({
            title: 'Not saved',
            message: `“${definition.label}” could not be changed. Please try again.`,
            variant: 'error',
          }),
      },
    );
  };

  if (isError) {
    return <p className="text-sm text-red-600">The automations could not be loaded.</p>;
  }

  return (
    <section aria-labelledby="automations-heading" className="mb-8 max-w-3xl">
      <h3 id="automations-heading" className="text-base font-semibold text-gray-900">
        Automations
      </h3>
      <p className="text-sm text-gray-600 mt-1 mb-3">
        Messages the app sends by itself — when something happens, or each morning at the daily task
        email&apos;s hour for the ones marked so. Each is sent once per occurrence; edit the wording
        in the templates below.
      </p>

      {GROUPS.map((group) => (
        <div key={group.title} className="mb-4">
          <h4 className="text-sm font-medium text-gray-800">{group.title}</h4>
          <p className="text-xs text-gray-500 mb-2">{group.note}</p>
          <ul className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
            {AUTOMATIONS.filter((a) => group.audiences.includes(a.audience)).map((entry) => {
              const definition: AutomationDefinition = entry;
              const setting = settings.get(entry.key)!;
              return (
                <li
                  key={definition.key}
                  className="px-3 py-2.5"
                  data-testid={`automation-${definition.key}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="pt-0.5">
                      <SettingToggle
                        checked={setting.enabled}
                        disabled={isLoading || update.isPending}
                        onChange={(enabled) => save(definition, { enabled })}
                        label={`${setting.enabled ? 'Turn off' : 'Turn on'}: ${definition.label}`}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900">
                        {definition.label}
                        {definition.schedule && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-normal text-gray-600">
                            Each morning
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {definition.trigger} · To: {AUDIENCE_LABELS[definition.audience]}
                      </div>
                      <div className="text-xs text-gray-400">
                        Wording: {definition.templates.join(', ')}
                      </div>
                      {setting.enabled && definition.schedule && (
                        <DaysField
                          schedule={definition.schedule}
                          value={setting.days ?? definition.schedule.defaultDays}
                          disabled={update.isPending}
                          label={definition.label}
                          onSave={(days) => save(definition, { days })}
                        />
                      )}
                      {setting.enabled &&
                        (definition.options ?? []).map((option) => (
                          <label
                            key={option.key}
                            className="mt-1.5 flex items-center gap-2 text-xs text-gray-700"
                          >
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600"
                              checked={setting.options[option.key] ?? option.defaultValue}
                              disabled={update.isPending}
                              onChange={(event) =>
                                save(definition, {
                                  options: { [option.key]: event.target.checked },
                                })
                              }
                            />
                            {option.label}
                          </label>
                        ))}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      <AutomationRunLog />
    </section>
  );
}

/**
 * A scheduled automation's number of days, read in its own sentence ("3 days
 * before it is due"). What is typed stays as typed; a whole number within the
 * automation's bounds is saved on Enter or on leaving the field, anything else
 * is marked and saves nothing.
 */
export function DaysField({
  schedule,
  value,
  disabled,
  label,
  onSave,
}: {
  schedule: AutomationSchedule;
  value: number;
  disabled?: boolean;
  label: string;
  onSave: (days: number) => void;
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);

  const parsed = /^\d+$/.test(text.trim()) ? Number(text.trim()) : NaN;
  const valid = Number.isInteger(parsed) && parsed >= schedule.min && parsed <= schedule.max;
  const [before, after = ''] = schedule.label.split('{n}');

  const commit = () => {
    if (!valid) return;
    if (parsed !== value) onSave(parsed);
  };

  return (
    <div className="mt-1.5 text-xs text-gray-700">
      <label className="flex flex-wrap items-center gap-1.5">
        {before.trim() && <span>{before.trim()}</span>}
        <input
          type="text"
          inputMode="numeric"
          aria-label={`Days for ${label}`}
          aria-invalid={!valid}
          className={`w-14 rounded border px-2 py-1 text-xs ${
            valid ? 'border-gray-300' : 'border-red-400 bg-red-50'
          }`}
          value={text}
          disabled={disabled}
          onChange={(event) => setText(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit();
            if (event.key === 'Escape') setText(String(value));
          }}
        />
        <span>{after.trim()}</span>
      </label>
      {!valid && (
        <p className="mt-0.5 text-red-600">
          A whole number from {schedule.min} to {schedule.max}.
        </p>
      )}
    </div>
  );
}

/** What the automations sent lately, newest first. */
function AutomationRunLog() {
  const { data: runs = [], isLoading } = useAutomationRuns();
  const [page, setPage] = useState(1);

  const columns: EntityListColumn<AutomationRun>[] = [
    {
      key: 'when',
      header: 'When',
      render: (run) => new Date(run.createdAt).toLocaleString(),
    },
    { key: 'automation', header: 'Automation', render: (run) => labelOf(run.automationKey) },
    {
      key: 'to',
      header: 'To',
      hideOnMobile: true,
      render: (run) => recipientText(run.recipient),
    },
    {
      key: 'result',
      header: 'Result',
      render: (run) => (
        <span title={run.detail ?? undefined}>
          <StatusBadge label={run.status} color={RUN_STATUS_COLORS[run.status]} />
        </span>
      ),
    },
  ];

  const shown = runs.slice((page - 1) * RUNS_PAGE_SIZE, page * RUNS_PAGE_SIZE);

  return (
    <div className="mt-4" data-testid="automation-runs">
      <h4 className="text-sm font-medium text-gray-800 mb-2">Recently sent</h4>
      {!isLoading && runs.length === 0 ? (
        <p className="text-xs text-gray-500">Nothing has been sent by an automation yet.</p>
      ) : (
        <EntityList
          items={shown}
          columns={columns}
          getKey={(run) => run.id}
          isLoading={isLoading}
          page={page}
          pageSize={RUNS_PAGE_SIZE}
          total={runs.length}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
